import { kvGet, kvSet, type Person } from '@routeback/core';
import { useEffect, useMemo, useState } from 'react';
import { useApp } from './app';
import { supabase } from './supabase';

/**
 * The company's people (company_people), cached on the device so manager
 * screens can show names with no signal. Local mode: just me.
 */
export function usePeople(): { people: Person[]; names: Map<string, string> } {
  const app = useApp();
  const key = `people:${app.company.id}`;
  const [people, setPeople] = useState<Person[]>(() => (supabase ? [] : [localMe(app.userId, app.userName, app.role)]));
  useEffect(() => {
    if (!supabase) {
      // Local mode: just me — plus anyone a test or demo put in the device cache.
      void kvGet<Person[]>(key).then((cached) => {
        if (cached?.length) setPeople([localMe(app.userId, app.userName, app.role), ...cached.filter((p) => p.user_id !== app.userId)]);
      });
      return;
    }
    let alive = true;
    void (async () => {
      const cached = await kvGet<Person[]>(key);
      if (alive && cached) setPeople(cached);
      const { data } = await supabase.rpc('company_people', { c: app.company.id });
      if (alive && data) { setPeople(data as Person[]); await kvSet(key, data); }
    })();
    return () => { alive = false; };
  }, [key, app.company.id, app.userId, app.userName, app.role]);
  const names = useMemo(() => new Map(people.map((p) => [p.user_id, p.display_name || p.email || 'Unnamed'])), [people]);
  return { people, names };
}

function localMe(id: string, name: string, role: Person['role']): Person {
  return { user_id: id, role, status: 'active', display_name: name, email: '', phone: '', hired_on: null, terminated_on: null } as Person;
}

/** "Mike Raitano" → "Mike R." (the plugin's schedule style). */
export function shortName(full: string): string {
  const parts = full.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]![0]}.` : parts[0] ?? '';
}
